"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import {
  ArrowRight,
  ArrowUpRight,
  Bell,
  Briefcase,
  Calendar,
  Check,
  DollarSign,
  FileText,
  LineChart,
  MapPin,
  Network,
  Plus,
  RefreshCw,
  Star,
  Trophy,
  User,
  Users,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { PublicNavGlass, type GlassNavMode } from "@/components/public-nav-glass"
import { HeroCtaPrimary, HeroCtaSecondary } from "@/components/glass/hero-cta"
import { HeroSearch } from "@/components/landing/hero-search"
import { GuestPassCard } from "@/components/landing/guest-pass-card"
import { ProductDemoCard, ZOOM_MS } from "@/components/landing/product-loop-video"
import { AutopayArt } from "@/components/landing/autopay-art"
import { CreditCardArt } from "@/components/landing/credit-card-art"
import { RewardsProgressArt } from "@/components/landing/rewards-progress-art"
import { LandingTractionTracker } from "@/components/landing/landing-traction-tracker"
import { EASE, Eyebrow, GlassCard, GlassSurface, Reveal, usePrefersReducedMotion, THEME } from "@/components/glass/glass-kit"
import { PulseDot } from "@/components/ui/pulse-dot"
import { useUserRole } from "@/providers/user-role-provider"
import { cn } from "@/lib/utils"
import type { PlatformStats } from "@/lib/platform-stats"

/**
 * Black-first public landing (design preview, not linked from the site).
 *
 * The whole surface is driven by the `--pg-*` custom properties below, which
 * the nav's sun/moon button flips between. Dark is the design target — an
 * all-black page built out of frosted glass panels, a fine grain overlay and
 * very low-contrast gloss — and light is the same layout in cream so the
 * toggle in the nav is honest.
 *
 * Copy is lifted from the live marketing dictionary (messages/en.json,
 * marketing.landing) so this reads as the real page, not lorem.
 */

/* ── Audience ──────────────────────────────────────────────────────
   The landing speaks to three people and the copy has to change for each
   one, not just the headline: an owner is filling chairs, a barber is
   finding one, a client is booking one. AUDIENCE holds a complete set of
   hero copy, CTAs and feature cards per mode so switching never leaves a
   half-translated page behind. */

type Audience = "barber" | "owner" | "client"

// Barber and Owner only. The Client tab came off the landing (2026-09-21):
// clients are part of the product, not a reader of this page, and a third
// tab read as a third business. The "client" branches below stay so the
// audience type is unchanged; nothing can select it from here.
const AUDIENCE_ORDER: { id: Audience; label: string }[] = [
  { id: "barber", label: "Barber" },
  { id: "owner", label: "Owner" },
]

// Hero CTA targets per audience. The LABELS come from the dictionary — only
// the destinations live in code.
const HERO_LINKS: Record<Audience, { primary: string; secondary: string }> = {
  owner: { primary: "/auth/signup?role=owner", secondary: "/hiring" },
  // Two different forms of the same job: primary is the manual browse,
  // secondary is the guided/plain-English one (/search) — not the job
  // board, which is a different feature (open positions, not open booths).
  barber: { primary: "/listings", secondary: "/search" },
  client: { primary: "/auth/signup/client", secondary: "/auth/login" },
}

// Audience colour, applied as a wash OVER the photo rather than a hue-rotate
// on it. Now that the backdrop is only lightly blurred, rotating the hue of a
// real storefront false-coloured it — a blue brick wall reads as broken, not
// as branding. A low-opacity overlay keeps the shop its true colour and still
// carries the mode's mood.
const HERO_TINT: Record<Audience, string> = {
  barber: "radial-gradient(75% 60% at 50% 40%, rgba(190,40,45,0.30) 0%, rgba(120,20,28,0.16) 55%, transparent 85%)",
  owner: "radial-gradient(75% 60% at 50% 40%, rgba(38,110,190,0.32) 0%, rgba(16,50,105,0.18) 55%, transparent 85%)",
  client: "radial-gradient(75% 60% at 50% 40%, rgba(228,228,232,0.16) 0%, rgba(150,150,158,0.10) 55%, transparent 85%)",
}

// Where the nav's Join button lands. Distinct from HERO_LINKS.primary, which
// for a barber is the listings rail rather than a signup.
const SIGNUP_HREF: Record<Audience, string> = {
  owner: "/auth/signup?role=owner",
  barber: "/auth/signup?role=barber",
  client: "/auth/signup/client",
}

// Feature-grid icons, index-aligned with features.<audience>.grid in the
// dictionary. Same mapping the previous landing used, so the icon next to a
// given card is unchanged.
const GRID_ICONS: Record<Audience, LucideIcon[]> = {
  owner: [Network, Briefcase, Users, Plus, RefreshCw, FileText],
  barber: [LineChart, User, Users, Briefcase, MapPin, FileText],
  // clientAccount ships three features, not six.
  client: [Calendar, Star, Users],
}

// Product screenshots, index-aligned with features.<audience>.products.
// Same CDN and same shots the live landing uses.
const CDN = "https://webflow-files-prod.global.ssl.fastly.net/67ae3561fb0d370bd1252062"
const PRODUCT_IMAGES: Record<"owner" | "barber", string[]> = {
  owner: [
    `${CDN}/67b3b72530ee5e8f74c2d386_Screenshot%202025-02-17%20at%204.24.29%20PM.avif`,
    `${CDN}/67affeecaed635590cca27f2_Screenshot%202025-02-14%20at%208.41.43%E2%80%AFPM.avif`,
    `${CDN}/67c9f4f716cf541f8901241a_Screenshot%202025-03-06%20at%201.17.02%E2%80%AFPM.avif`,
  ],
  barber: [
    `${CDN}/67b3b3e2205bf4cb1f918af1_Screenshot%202025-02-17%20at%204.10.37%20PM.avif`,
    `${CDN}/67b3b4f2fbb463fee505ba22_Screenshot%202025-02-17%20at%204.15.09%20PM.avif`,
    `${CDN}/67b3b20706d971a678088258_Screenshot%202025-02-17%20at%204.02.41%20PM.avif`,
  ],
}

// Product loops per audience — MP4s in public/videos/, index-aligned with
// features.<audience>.products. All are portrait phone recordings encoded at
// 960px wide, 30fps, H.264 CRF 27, no audio (the 2574×3218 originals were
// 1–4 MB each; these are 80–350 KB) with a first-frame WebP poster
// (PRODUCT_PHONE_ASPECT), so the card crops and zooms them identically; a
// playbackRate is set only where a recording moves too fast for the card.
// Client has no products row, so no entry.
type ProductVideo = { src: string; poster?: string; playbackRate?: number }
const PRODUCT_VIDEOS: Record<"owner" | "barber", ProductVideo[]> = {
  owner: [
    { src: "/videos/manage-every-shop.mp4", poster: "/videos/manage-every-shop-poster.webp", playbackRate: 0.68 },
    { src: "/videos/control-system.mp4", poster: "/videos/control-system-poster.webp" },
    { src: "/videos/barber-profiles.mp4", poster: "/videos/barber-profiles-poster.webp" },
  ],
  barber: [
    { src: "/videos/service-options.mp4", poster: "/videos/service-options-poster.webp" },
    { src: "/videos/booth-dashboard.mp4", poster: "/videos/booth-dashboard-poster.webp" },
    { src: "/videos/rewards-hub.mp4", poster: "/videos/rewards-hub-poster.webp" },
  ],
}

// Hero backdrop — real shops shot by the BOOTH team, one per audience, sitting
// under the drifting-light gradient. No stock interiors.
//
// Every candidate below RESOLVES. This list used to guess five extensions per
// variant (slckr.avif, .jpg, .jpeg, .png, .webp) and walk them on load error,
// so owner and client each burned four or five sequential 404s before the
// browser so much as requested the photo that exists — which is exactly the
// stretch where the hero showed as a bare gradient. Candidates are authored
// against files that are really in the repo or the bucket now, so the first
// request is the right one and it can be preloaded. When a local photo lands
// in public/images/shops/, add it here with its real extension.
//
// NEXT_PUBLIC_HERO_BG_URL still wins for every variant when set, for pointing
// the whole hero at one hosted image instead of repo files.
// Covers live in the same Supabase bucket HeroShopCycle rotates through, so
// every fallback below is a real shop already on BOOTH.
const SHOP_COVERS =
  "https://huuicdgepphevimfjgus.supabase.co/storage/v1/object/public/shop-images/covers"

/** Local drop-ins are tried first, then a hosted cover, so a mode is never
    blank while its photo is still being shot. */
const heroSources = (names: string[], fallback: string) =>
  [
    process.env.NEXT_PUBLIC_HERO_BG_URL,
    ...names.map((n) => `/images/shops/${n}`),
    `${SHOP_COVERS}/${fallback}`,
  ].filter(Boolean) as string[]

// 20px-wide JPEG of each hero, inlined. It ships inside the HTML, so the first
// paint is already the shop's own colour and light instead of the flat blue
// gradient that used to sit there until the photo arrived. Regenerate one of
// these whenever its photo changes, or the hero will fade in from the wrong
// picture — scripts/hero-blur.py prints a replacement.
const HERO_BLUR: Record<Audience, string> = {
  barber:
    "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDABIMDRANCxIQDhAUExIVGywdGxgYGzYnKSAsQDlEQz85Pj1HUGZXR0thTT0+WXlaYWltcnNyRVV9hnxvhWZwcm7/2wBDARMUFBsXGzQdHTRuST5Jbm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm7/wAARCAAkABQDASIAAhEBAxEB/8QAGAAAAwEBAAAAAAAAAAAAAAAAAAMFBAb/xAAmEAACAQMDAwQDAAAAAAAAAAABAgMABBEFITESIkETQlGxFDJx/8QAFwEAAwEAAAAAAAAAAAAAAAAAAAIDAf/EABsRAAICAwEAAAAAAAAAAAAAAAABAhEDEiFR/9oADAMBAAIRAxEAPwB2k6wCuC4Y+4eT/c/dV5RFf256SM+D8GuNaFc9q7jfIODT7PUJ7eVe8kZxlhuKRSLSw+HUR3k9uoijjRgoAOWxg44oqYmuW7g+tHlwSM7/ADRTWiWr8JEsbdcZ9Mt+rDA5Gd/NKtoGjdmLAAuDknJAztsK12VtNKpVAodRx1iqltYBJAZwiofYg+zSpcLylG9kTorSVgzR2sjgsckL5zRV38houwOoC7YJoopCrI1w0Gyt7a3dIYwgUhgRznisErkybnnmiitQjFncnNFFFBh//9k=",
  owner:
    "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDABIMDRANCxIQDhAUExIVGywdGxgYGzYnKSAsQDlEQz85Pj1HUGZXR0thTT0+WXlaYWltcnNyRVV9hnxvhWZwcm7/2wBDARMUFBsXGzQdHTRuST5Jbm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm7/wAARCAANABQDASIAAhEBAxEB/8QAFwABAQEBAAAAAAAAAAAAAAAAAAUEBv/EACEQAAICAgICAwEAAAAAAAAAAAECAxEAEgQFE1EhMUHR/8QAFgEBAQEAAAAAAAAAAAAAAAAAAQAC/8QAFxEBAQEBAAAAAAAAAAAAAAAAAAECEv/aAAwDAQACEQMRAD8AlRRpLPGkcVEKpJ+7NYVtZHQuyAAGq/crdPKOBGXdfKHWioOv9zFLxjyCJEfxmVdqqwB6wuhy7DqU48/V8aaONakjDXrVn3jHSR+DpuJHttrHV1V/Jxk1H//Z",
  client:
    "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDABIMDRANCxIQDhAUExIVGywdGxgYGzYnKSAsQDlEQz85Pj1HUGZXR0thTT0+WXlaYWltcnNyRVV9hnxvhWZwcm7/2wBDARMUFBsXGzQdHTRuST5Jbm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm7/wAARCAAZABQDASIAAhEBAxEB/8QAGAAAAwEBAAAAAAAAAAAAAAAAAAQFAwb/xAAmEAACAgIBAwMFAQAAAAAAAAABAgMEABESBSExIjJxEzNikaHw/8QAFwEBAQEBAAAAAAAAAAAAAAAAAAIBA//EABgRAQADAQAAAAAAAAAAAAAAAAABERIh/9oADAMBAAIRAxEAPwDHqHXYYr8girpI0UrcCjel1I7f4YiE6nfrohEorA9ljTiuz+R85vVk+lQsq0MbSzyhQ5OmXSg6HbxjNzrlyKhXhWKIRJx0+yTtccLklBaXoitVnrI7E8weQOgR4/mGT7Vt7Mxkk1vWu2GKLWJac6Sjg4aEMW0R6t615ydbM7IEmrspBBBX2/P6zpH8n5zE+05GnTEIFWtXmRmmJVuWgAdDWGMy/db5wzUv/9k=",
}

const HERO_SHOPS: Record<Audience, { alt: string; sources: string[] }> = {
  // Rebels ships in-repo; LineUp's single styled chair stands in behind it,
  // which is the thing a barber is shopping for anyway.
  barber: {
    alt: "Rebels 108 barbershop storefront in Mexico City",
    sources: heroSources(["rebels.avif"], "lineup-salon-cover.png"),
  },
  // Starlite in Roma Norte, CDMX: mint facade, hand-painted "Walk-ins Welcome
  // / Appointments Always" lettering, the shop's own signage. An owner is
  // being sold a business, not a haircut, and a premises with the paint and
  // the sign on is the closest thing to a picture of one. It also carries the
  // same colour and craft as the barber hero a tab away, so the switch reads
  // as one brand rather than two moods.
  owner: {
    alt: "Starlite Barber Shop storefront in Roma Norte, Mexico City",
    sources: heroSources([], "starlite-cdmx-cover.png"),
  },
  // Project Barber, Battersea. Black shopfront, the name across the fascia and
  // BARBER SHOP going up in gold-and-red leaf on the glass while a signwriter
  // is still on the ladder. A client's relationship to a shop is the street
  // outside it, and the checkerboard pavement plus the blue neighbour keep it
  // off monochrome. Note this cover is also the third SPOTLIGHT card below.
  client: {
    alt: "Project Barber storefront in Battersea, London",
    sources: heroSources([], "6e3c009d-0e84-4047-a927-edf5fd8081f8-1778551431363.png"),
  },
}

/* Hero parallax. The backdrop drifts down at a fraction of the scroll rate so
   it lags the copy moving up. Reads rAF-throttled so it never lands more than
   one transform per frame, and returns a flat 0 when the visitor has asked for
   reduced motion — a drifting background is exactly what that setting is for.

   Travel is capped at one viewport's worth of scroll: past that the hero is
   off screen, and letting the value grow would eventually drag the image's
   edge into view. */
const PARALLAX_RATE = 0.15

function useHeroParallax() {
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)")
    if (reduced.matches) return

    let frame = 0
    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        const travel = Math.min(window.scrollY, window.innerHeight)
        setOffset(travel * PARALLAX_RATE)
      })
    }

    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [])

  return offset
}

// Spotlight cards — image, eyebrow + headline over the top of it, and a
// corner jump button. These are REAL shops on BOOTH: the covers come from
// the same Supabase bucket HeroShopCycle rotates through, and each card
// links to that shop's actual listing. No stock photography here — the
// section claims these are shops on the platform, so they have to be.
//
// The shops are fixed; the eyebrow + headline laid over them are not. Those
// come from browse.cards.<audience>, index-aligned with this array, because a
// barber is being sold chairs to rent and an owner is being sold barbers to
// hire off the very same photos.
const SPOTLIGHT = [
  {
    src: "https://huuicdgepphevimfjgus.supabase.co/storage/v1/object/public/shop-images/covers/starlite-cdmx-cover.png",
    alt: "Starlite Barber Shop storefront in Roma Norte, CDMX",
    shop: "Starlite Barber Shop",
    place: "Mexico City, MX",
    href: "/listings/starlite-cdmx",
  },
  {
    src: "https://huuicdgepphevimfjgus.supabase.co/storage/v1/object/public/shop-images/covers/sanctuary-atl-cover.png",
    alt: "Sanctuary ATL Barber Co. storefront in Atlanta",
    shop: "Sanctuary ATL",
    place: "Atlanta, US",
    href: "/listings/sanctuary-atl",
  },
  {
    src: "https://huuicdgepphevimfjgus.supabase.co/storage/v1/object/public/shop-images/covers/6e3c009d-0e84-4047-a927-edf5fd8081f8-1778551431363.png",
    alt: "Project Barber storefront in Battersea, London",
    shop: "Project Barber",
    place: "London, UK",
    href: "/listings/project-barber",
  },
]

const PLATFORMS = [
  { src: "/booking-logos/booksy.svg", alt: "Booksy", href: "https://biz.booksy.com/en-us" },
  { src: "/booking-logos/squire.svg", alt: "Squire", href: "https://getsquire.com/" },
  { src: "/booking-logos/thecut.svg", alt: "The Cut", href: "https://thecut.co/" },
  { src: "/booking-logos/fresha.svg", alt: "Fresha", href: "https://www.fresha.com/" },
  { src: "/booking-logos/fullcap.svg", alt: "FullCap", href: "https://fullcap.ai/" },
]


/* The barber / owner / client switch. Lives under the glass nav, fixed, so
   it is in the same place at every scroll position — the sections it drives
   (features, products, hiring) all sit below the fold. */
function AudienceTabs({
  audience,
  onAudienceChange,
  className = "",
}: {
  audience: Audience
  onAudienceChange: (a: Audience) => void
  className?: string
}) {
  return (
    <div
      role="tablist"
      aria-label="Who are you?"
      className={`inline-flex items-center gap-[3px] rounded-full p-[3px] backdrop-blur-xl ${className}`}
      // No edge: the card-edge token is near-white in light mode and drew a
      // bright ring around the switch. The fill alone is the shape.
      style={{ background: "var(--pg-card)" }}
    >
      {AUDIENCE_ORDER.map((a) => {
        const active = a.id === audience
        return (
          <button
            key={a.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onAudienceChange(a.id)}
            className="rounded-full px-3 py-1 text-[10px] font-medium transition-all duration-150"
            style={
              active
                ? { background: "var(--pg-invert-bg)", color: "var(--pg-invert-fg)" }
                : { background: "transparent", color: "var(--pg-muted)" }
            }
          >
            {a.label}
          </button>
        )
      })}
    </div>
  )
}

export default function LandingGlassClient({ platformStats }: { platformStats: PlatformStats }) {
  const [mode, setMode] = useState<GlassNavMode>("dark")
  const [audience, setAudience] = useState<Audience>("owner")

  // Switching audience is a new page, not a repaint: every section below the
  // hero changes copy, cards and loops, so landing mid-way down the previous
  // tab's page put the reader in the middle of a story that just changed.
  // Reset to the top on every switch. Instant, not smooth — a 3000px smooth
  // scroll while the sections swap underneath reads as the page collapsing.
  const changeAudience = useCallback(
    (next: Audience) => {
      if (next !== audience) window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior })
      setAudience(next)
    },
    [audience],
  )

  // Ad traffic arrives already segmented. ?for=barber opens the barber page —
  // its headline, its sections, its search field — instead of the owner pitch
  // a barber has to click past. One URL per audience, no duplicate pages to
  // keep in sync, and the parameter is ignored if it is anything else.
  //
  // In an effect, not a useState initialiser: this component is
  // server-rendered, and seeding at init would render owner on the server and
  // barber on the client.
  useEffect(() => {
    const forParam = new URLSearchParams(window.location.search).get("for")
    if (forParam === "barber" || forParam === "owner" || forParam === "client") {
      setAudience(forParam)
    }
  }, [])

  // GlassSurface owns the token table, the grain layer and the iOS
  // status-bar strip — everything that used to be hand-rolled here.
  return (
    <GlassSurface mode={mode}>
      <PublicNavGlass
        mode={mode}
        onModeChange={setMode}
        audience={audience}
        signUpHref={SIGNUP_HREF[audience]}
        below={<AudienceTabs audience={audience} onAudienceChange={changeAudience} />}
      />

      <Hero mode={mode} audience={audience} />
      <Mission />
      <Features audience={audience} />
      <Money audience={audience} />
      <Spotlight audience={audience} />
      <Hiring audience={audience} />
      <GuestPass audience={audience} />
      <Booking audience={audience} />
      <Support audience={audience} />
      {audience !== "client" && <LandingTractionTracker stats={platformStats} />}
      <FinalCta audience={audience} />
      <Footer mode={mode} audience={audience} />
    </GlassSurface>
  )
}

/* ── Hero ──────────────────────────────────────────────────────── */

/* The hero's photograph.

   Three things kept the old raw <img> from being on screen when the page was:
   it went through no optimizer, so a 900KB–2MB PNG went over the wire at full
   size; the browser had no reason to fetch it early, since it learned about it
   only when the markup parsed and it ranked as an ordinary image; and for two
   of the three audiences it first had to fail its way through a list of
   guessed filenames. Meanwhile the layers stacked over it — the audience wash,
   the scrim, the drifting light — rendered instantly, so the wait read as a
   deliberate blue-grey screen that a photo later replaced.

   So: next/image serves AVIF at the size actually needed — the owner hero is
   about 10KB where the raw PNG behind it is 1.3MB — `priority` puts a preload
   for it in the document head —
   which is why the candidate list has to lead with a URL that resolves, since
   the preload only ever points at the first one — and an inlined 20px JPEG of
   the same shop sits underneath from the first paint. What's left of the wait
   now looks like the photo out of focus, not like a different screen.

   The layer still removes itself if every candidate 404s, so a mode whose
   photo has been pulled degrades to the plain gradient rather than a broken
   frame. */
function HeroBackdrop({
  audience,
  isDark,
  parallax,
}: {
  audience: Audience
  isDark: boolean
  parallax: number
}) {
  const shop = HERO_SHOPS[audience]
  const [index, setIndex] = useState(0)

  // Every candidate failed — nothing left to show.
  if (index >= shop.sources.length) return null

  return (
    // Taller than the hero and pulled up, so the parallax drift has room to
    // travel without exposing an edge.
    //
    // The thumbnail is painted here rather than handed to next/image as
    // `placeholder="blur"`: that path wraps the data URL in an SVG carrying
    // two more 20px gaussians of its own, which on top of the 7px this layer
    // already applies left almost nothing on screen. As a plain background it
    // is simply the same picture, softer, with the photo landing over it.
    <div
      aria-hidden
      data-bg-alt={shop.alt}
      data-focus={audience}
      className="hero-photo-wrap pointer-events-none absolute inset-x-0 top-[-20%] h-[140%] select-none"
      style={{
        backgroundImage: `url("${HERO_BLUR[audience]}")`,
        backgroundSize: "cover",
        backgroundPosition: "var(--hero-focus, center)",
        // Full opacity in light mode: at 0.8 the cream page showed through
        // the photo and that, more than the blur, was the haze.
        opacity: isDark ? 0.92 : 1,
        // A soft 3px, down from 7px: the shop reads as a shop, the type still
        // sits in front of it. On the wrapper so the thumbnail and the photo
        // get the same treatment; the 1.06 scale keeps the blur's soft edge
        // off-screen. Light mode drops the 0.9 brightness, which only greyed
        // the picture there.
        filter: isDark
          ? "blur(3px) contrast(1.04) saturate(1.08) brightness(0.9)"
          : "blur(3px) contrast(1.05) saturate(1.1)",
        transform: `scale(1.06) translate3d(0, ${parallax}px, 0)`,
        willChange: "transform",
      }}
    >
      <Image
        key={shop.sources[index]}
        src={shop.sources[index]}
        alt=""
        fill
        priority
        // Blurred only 3px now, so a narrow low-quality candidate shows its
        // pixels; full viewport width at quality 70 keeps the shop crisp
        // enough to read as a photo.
        sizes="100vw"
        quality={70}
        onError={() => setIndex((i) => i + 1)}
        // Where the crop sits on a phone (globals.css .hero-photo): the
        // narrow viewport shows about half the photo's width, and for the
        // barber shop that centred crop cut the pole off the right edge.
        data-focus={audience}
        className="hero-photo object-cover"
      />
    </div>
  )
}

function Hero({ mode, audience }: { mode: GlassNavMode; audience: Audience }) {
  // The hero does not follow the sun/moon toggle. It is a photograph with
  // type on it, and the light treatment put a cream film over the shop and
  // flipped the headline to near-black — which read as a different page
  // rather than the same one lit differently. The toggle still drives
  // everything below the fold; here the dark tokens are pinned, so the
  // backdrop, the scrim, the badge, the CTAs and the switch all stay the
  // one version of themselves. `mode` is still taken as a prop because the
  // section's own edge (the hairline horizon) sits against the page under it.
  const isDark = true
  const t = useTranslations("marketing.landing")
  // Client has no landing block of its own — it reuses the copy the
  // standalone client door already ships.
  const tc = useTranslations("marketing.clientAccount")
  const links = HERO_LINKS[audience]
  const ctaSize = audience === "barber" ? "compact" : "default"
  const tint = HERO_TINT[audience]
  const parallax = useHeroParallax()
  const router = useRouter()
  const { userId } = useUserRole()
  // "Post an opening" needs an owner account before it means anything — a
  // logged-out click prompts signup (pre-set to the owner role) instead of
  // dropping an anonymous visitor onto the manage view of /hiring.
  // "Post an opening" is a signed-in action: an owner goes straight to the
  // job board, a visitor goes to sign in and comes back to it. Was a signup
  // modal, which on the phone read as landing on the job board.
  const handleOwnerSecondaryClick = useCallback(() => {
    router.push(userId ? links.secondary : `/auth/login?redirect=${encodeURIComponent(links.secondary)}`)
  }, [userId, router, links.secondary])

  const copy =
    audience === "client"
      ? {
          headline: [tc("title"), ""] as const,
          sub: tc("subtitle"),
          primary: tc("createAccount"),
          secondary: tc("signIn"),
        }
      : {
          headline: [
            t(`hero.${audience}.headlineLine1`),
            t(`hero.${audience}.headlineLine2`),
          ] as const,
          sub: t(`hero.${audience}.subhead`),
          primary: t(`hero.${audience}.cta`),
          // Barber's secondary CTA is its own key now — it used to reuse
          // hiring.barber.cta ("Browse open positions"), which is the job
          // board's own CTA further down the page and points at /hiring,
          // not /search. Sharing the string meant renaming one silently
          // renamed the other.
          secondary: audience === "owner" ? t("hiring.owner.cta") : t("hero.barber.secondaryCta"),
        }
  return (
    <section
      className="relative flex min-h-svh items-center justify-center overflow-hidden px-5 pb-30 pt-28"
      // 120svh on the phone so the first screen is the shop photo edge to
      // edge — at 100svh the section under it showed as a black band above
      // Safari's toolbar. Desktop keeps the min-h-svh utility.
      style={{
        minHeight: "max(100svh, var(--hero-mobile-h, 0px))",
        // Pinned dark tokens for everything inside the hero.
        ...(THEME.dark as React.CSSProperties),
      }}
    >
      {/* Shop photography behind everything — a different real shop per
          audience. Keyed on audience so React swaps the element (and its
          src-walk state) cleanly on switch. */}
      <HeroBackdrop key={audience} audience={audience} isDark={isDark} parallax={parallax} />
      {/* Audience colour wash over the photo (see HERO_TINT). Pulled back a
          third — see the scrim below. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: tint, mixBlendMode: isDark ? "screen" : "multiply", opacity: isDark ? 0.38 : 0.16 }}
      />
      {/* Scrim over the photo. Every stop here came down by about a third, so
          the shop reads as a room rather than something behind frosted glass.
          The one stop still doing real work is the bottom, which carries the
          hero into the black page under it; the headline holds its contrast on
          its own gradient fill. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: isDark
            ? "linear-gradient(180deg, rgba(5,5,6,0.14) 0%, rgba(5,5,6,0.05) 42%, rgba(5,5,6,0.62) 100%)"
            : "linear-gradient(180deg, rgba(239,237,231,0) 0%, rgba(239,237,231,0) 55%, rgba(239,237,231,0.55) 100%)",
        }}
      />

      {/* Slow-drifting light. Two very low-opacity ellipses is all the
          colour this page gets — everything else is black on black. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: isDark
            ? "radial-gradient(60% 50% at 50% 8%, rgba(255,255,255,0.09) 0%, transparent 70%), radial-gradient(45% 40% at 82% 78%, rgba(255,255,255,0.07) 0%, transparent 72%), radial-gradient(50% 45% at 12% 82%, rgba(255,255,255,0.05) 0%, transparent 70%)"
            : "radial-gradient(60% 50% at 50% 8%, rgba(255,255,255,0.28) 0%, transparent 70%), radial-gradient(45% 40% at 82% 78%, rgba(255,255,255,0.16) 0%, transparent 72%)",
          animation: "pgDrift 26s ease-in-out infinite alternate",
        }}
      />
      {/* Reading wash: a soft oval behind the copy only, so the headline and
          buttons keep their contrast now that the photo is clearer. The
          edges of the frame stay pure photo. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: isDark
            ? "radial-gradient(70% 34% at 50% 60%, rgba(5,5,6,0.42) 0%, rgba(5,5,6,0.18) 55%, transparent 80%)"
            : "radial-gradient(70% 34% at 50% 60%, rgba(239,237,231,0.55) 0%, rgba(239,237,231,0.22) 55%, transparent 80%)",
        }}
      />
      {/* Hairline horizon so the hero has an edge to sit on. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent, var(--pg-line) 22%, var(--pg-line) 78%, transparent)" }}
      />

      <div className="relative z-10 mx-auto w-full max-w-3xl text-center">
        <Reveal>
          <span
            // Light weight and near-normal tracking: at 10px caps the old
            // medium/0.16em read as a wide label competing with the headline
            // under it, which is the one thing on this screen meant to shout.
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-light uppercase tracking-[0.06em] backdrop-blur-xl"
            style={{
              background: "var(--pg-card)",
              border: "1px solid var(--pg-card-edge)",
              color: "var(--pg-muted)",
            }}
          >
            <PulseDot className="h-[3px] w-1.5" />
            {t("hero.badge")}
          </span>
        </Reveal>

        <Reveal delay={90}>
          <h1
            className="mt-7 text-[clamp(2.75rem,9vw,5.25rem)] font-semibold leading-[0.95] tracking-[-0.045em]"
            style={{
              backgroundImage: "linear-gradient(175deg, var(--pg-fg) 34%, var(--pg-muted) 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {copy.headline[0]}
            {copy.headline[1] ? (
              <>
                <br />
                {copy.headline[1]}
              </>
            ) : null}
          </h1>
        </Reveal>

        <Reveal delay={170}>
          {/* text-balance: no single word stranded on the last line (RULES.md,
              "UI — no orphans"). "…barbers and shop / owners." was the case. */}
          <p className="mx-auto mt-6 max-w-lg text-balance text-[15px] leading-relaxed sm:text-base" style={{ color: "var(--pg-muted)" }}>
            {copy.sub}
          </p>
        </Reveal>

        {/* Barbers who already know what BOOTH is want to type a city and go,
            rather than read the pitch to reach a button. Barber only: /search
            is work search, so it is not where an owner or a client is headed. */}
        {audience === "barber" && (
          <Reveal delay={210}>
            <HeroSearch />
          </Reveal>
        )}

        <Reveal delay={240}>
          {/* Side by side at every width. On the barber hero there is one
              compact button, the same h-9 as the search field above it. */}
          <div className={`flex flex-wrap items-center justify-center ${audience === "barber" ? "mt-6 gap-2" : "mt-9 gap-3"}`}>
            {/* The site's CTA pair (components/glass/hero-cta.tsx) — premium
                sheen-over-gloss hover. Audience-agnostic by construction —
                this is the one pair the page renders, so owner, barber and
                client all get it. */}
            <HeroCtaPrimary href={links.primary} icon={ArrowRight} size={ctaSize}>
              {copy.primary}
            </HeroCtaPrimary>
            {/* No secondary on the barber hero. It was "Search shops", and
                sitting right under the search field it read as that field's
                submit button. The field is the search, so the one button
                under it is the other way in: browse every shop. */}
            {audience !== "barber" && (
            <HeroCtaSecondary
              size={ctaSize}
              href={links.secondary}
              icon={audience === "owner" ? Plus : undefined}
              onClick={
                audience === "owner"
                  ? (e) => {
                      e.preventDefault()
                      handleOwnerSecondaryClick()
                    }
                  : undefined
              }
            >
              {copy.secondary}
            </HeroCtaSecondary>
            )}
          </div>
        </Reveal>

      </div>

      <style jsx>{`
        @keyframes pgDrift {
          0% {
            transform: translate3d(0, 0, 0) scale(1);
          }
          100% {
            transform: translate3d(0, -2.5%, 0) scale(1.08);
          }
        }
      `}</style>

    </section>
  )
}

/* ── Mission ───────────────────────────────────────────────────── */

function Mission() {
  const t = useTranslations("marketing.landing")
  const reduced = usePrefersReducedMotion()
  const blockRef = useRef<HTMLDivElement>(null)

  const text = t("mission.text")
  const words = text.split(/\s+/).filter(Boolean)

  const titleClass =
    "flex flex-wrap justify-center gap-x-[0.28em] gap-y-1 text-pretty font-normal leading-[1.28] tracking-[-0.025em] text-[clamp(1.35rem,5.4vw,2rem)] sm:leading-[1.22] sm:text-[clamp(1.85rem,3.9vw,3.15rem)]"

  // No pin. The paragraph sits in the page and the words light as it rises
  // through the viewport — the fill is a function of where the block is,
  // not of a scroll-jacked stretch the reader has to push through. The
  // pinned 165svh version read as the page getting stuck on the phone.
  //
  // Browsers with scroll-driven animations (Safari 26, Chrome) run the whole
  // thing on the compositor from CSS (.mission-word / .mission-eyebrow in
  // globals.css, keyed on the block's own view timeline); this effect is the
  // fallback for the rest: one --p write per frame from the block's position.
  useEffect(() => {
    if (reduced) return
    if (typeof CSS !== "undefined" && CSS.supports("animation-timeline: view()")) return
    const el = blockRef.current
    if (!el) return
    let raf = 0
    const tick = () => {
      raf = 0
      const r = el.getBoundingClientRect()
      const vh = window.innerHeight
      // 0 as the block's top enters at the bottom edge → 1 once it has
      // travelled 55% of the way through (same span as the CSS ranges).
      const p = Math.min(1, Math.max(0, (vh - r.top) / (0.55 * (vh + r.height))))
      el.style.setProperty("--p", p.toFixed(4))
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(tick)
    }
    tick()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
    }
  }, [reduced])

  if (reduced) {
    return (
      <section className="px-5 py-20 sm:px-6 sm:py-32">
        <div className="mx-auto w-full max-w-3xl text-center">
          <Eyebrow>{t("mission.badge")}</Eyebrow>
          <h2 className={`mt-5 ${titleClass}`} style={{ color: "var(--pg-fg)" }}>
            {text}
          </h2>
        </div>
      </section>
    )
  }

  return (
    <section className="px-5 py-24 sm:px-6 sm:py-36">
      <div ref={blockRef} className="mission-block mx-auto w-full max-w-3xl text-center" style={{ ["--p" as string]: 0 }}>
        <div className="mission-eyebrow">
          <Eyebrow>{t("mission.badge")}</Eyebrow>
        </div>
        <h2 className={`mt-4 sm:mt-6 ${titleClass}`}>
          {words.map((word, i) => {
            const start = (i / words.length) * 0.92
            const end = start + 2.1 / words.length
            return (
              <span
                key={`${word}-${i}`}
                className="mission-word inline-block"
                style={{
                  ["--s" as string]: start.toFixed(4),
                  ["--e" as string]: end.toFixed(4),
                  // The same window as percentages of the block's cover range,
                  // for the CSS timeline (see globals.css).
                  ["--s-pct" as string]: `${(start * 55).toFixed(2)}%`,
                  ["--e-pct" as string]: `${(Math.min(end, 1) * 55).toFixed(2)}%`,
                }}
              >
                {word}
              </span>
            )
          })}
        </h2>
      </div>
    </section>
  )
}

/* ── Spotlight cards ───────────────────────────────────────────── */

// Snap rail of image cards: one card per screen on mobile, three across on
// desktop. Dots below track the snap position, then a single glossy CTA and
// a quiet caption line underneath — the card pattern from the mock.
function Spotlight({ audience }: { audience: Audience }) {
  const t = useTranslations("marketing.landing")
  const railRef = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(0)

  const onScroll = () => {
    const el = railRef.current
    if (!el) return
    const card = el.firstElementChild as HTMLElement | null
    if (!card) return
    // Card width + the flex gap — derived from the DOM so it stays correct
    // across the responsive width changes.
    const step = card.offsetWidth + parseFloat(getComputedStyle(el).columnGap || "0")
    setIndex(Math.min(SPOTLIGHT.length - 1, Math.max(0, Math.round(el.scrollLeft / step))))
  }

  const goTo = (i: number) => {
    const el = railRef.current
    if (!el) return
    const card = el.firstElementChild as HTMLElement | null
    if (!card) return
    const step = card.offsetWidth + parseFloat(getComputedStyle(el).columnGap || "0")
    el.scrollTo({ left: i * step, behavior: "smooth" })
  }

  // Renting a chair and hiring for one are both pro concerns — a client has
  // no use for either, so the whole rail sits out that mode.
  if (audience === "client") return null

  // Owners are shopping for barbers, barbers for booths: same photos, opposite
  // side of the marketplace, so heading, copy and destination all flip.
  const isOwner = audience === "owner"
  const cards = t.raw(`browse.cards.${audience}`) as { eyebrow: string; title: string }[]
  const heading = isOwner ? t("browse.headingOwner") : t("browse.heading")
  const subtitle = isOwner ? t("browse.subtitleOwner") : t("browse.subtitle")
  const browseHref = isOwner ? "/barbers" : "/listings"

  return (
    <section className="px-5 py-16 sm:py-24">
      <div className="mx-auto max-w-5xl">
        <Reveal>
          <div className="mb-8 flex items-end justify-between gap-6">
            <div>
              <Eyebrow mark tone="muted">
                {subtitle}
              </Eyebrow>
              <h2 className="mt-4 text-[clamp(1.7rem,4vw,2.4rem)] font-semibold leading-[1.08] tracking-[-0.035em]">
                {heading}
              </h2>
            </div>
            <Link
              href={browseHref}
              className="hidden shrink-0 items-center gap-1.5 text-[13px] font-medium transition-opacity hover:opacity-60 sm:inline-flex"
            >
              {t("browse.viewAll")}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div
            ref={railRef}
            onScroll={onScroll}
            className="scrollbar-hide -mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-1"
            // A scroll container is its own compositing layer, and the grain
            // (mix-blend-overlay, in GlassSurface) blends against a transparent
            // layer differently than against the page: in light mode the rail
            // showed as a paler rectangle behind the cards. Painting the page
            // colour here gives the blend the same backdrop as everywhere else.
            style={{ scrollbarWidth: "none", background: "var(--pg-bg)" }}
          >
            {SPOTLIGHT.map((card, i) => (
              <Link
                key={card.href}
                href={card.href}
                className="group relative w-[82vw] max-w-[380px] shrink-0 snap-center sm:w-[calc((100%-1rem)/2)] lg:w-[calc((100%-2rem)/3)]"
              >
                <div
                  className="relative aspect-[3/4] overflow-hidden rounded-[26px]"
                  style={{
                    border: "1px solid var(--pg-card-edge)",
                    boxShadow: "0 30px 70px -40px rgba(0,0,0,0.95)",
                  }}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={card.src}
                    alt={card.alt}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
                    style={{ transitionTimingFunction: EASE }}
                  />
                  {/* Legibility scrim — heavy at the top where the copy sits,
                      light wash at the bottom to seat the card on the page. */}
                  <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.22) 38%, transparent 62%, rgba(0,0,0,0.5) 100%)",
                    }}
                  />

                  <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-3 p-5">
                    <div className="min-w-0">
                      <p className="text-[12px] font-medium text-white/70">{cards[i]?.eyebrow}</p>
                      <h3 className="mt-1.5 text-balance text-[17px] font-semibold leading-[1.25] tracking-[-0.015em] text-white">
                        {cards[i]?.title}
                      </h3>
                    </div>
                    <span
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full backdrop-blur-xl transition-transform duration-300 group-hover:rotate-45"
                      style={{ background: "rgba(255,255,255,0.9)", color: "#0A0A0A" }}
                    >
                      <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
                    </span>
                  </div>

                  {/* Location only — the shop name was truncating at this
                      width and the city is what actually sells the card. */}
                  <div className="absolute inset-x-0 bottom-0 flex items-center p-5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/80">
                    <span className="truncate">{card.place}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Reveal>

        {/* Dots */}
        <div className="mt-6 flex items-center justify-center gap-1.5">
          {SPOTLIGHT.map((card, i) => (
            <button
              key={card.href}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Go to card ${i + 1}`}
              className="h-1.5 rounded-full transition-all duration-300"
              style={{
                width: i === index ? 18 : 6,
                background: i === index ? "var(--pg-fg)" : "var(--pg-line)",
              }}
            />
          ))}
        </div>

        {/* Glossy CTA + caption — the button treatment from the mock, in
            the page's inverted neutral. No green anywhere on this surface. */}
        <Reveal delay={120}>
          {/* House button scale (h-10 / text-sm), hugging its label instead of
              spanning the column — a rail's "see the rest" affordance, not the
              page's primary action. */}
          <div className="mt-8 flex flex-col items-center">
            <Link
              href={browseHref}
              className="relative inline-flex h-10 items-center justify-center gap-1.5 overflow-hidden rounded-full px-5 text-sm font-medium tracking-tight transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: "var(--pg-invert-bg)",
                color: "var(--pg-invert-fg)",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.30), inset 0 -1px 0 rgba(0,0,0,0.22), 0 12px 28px -20px rgba(0,0,0,0.85)",
              }}
            >
              <span className="relative">{t("browse.viewAll")}</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
            {/* What BOOTH costs is a shop's question — barbers pay rent to the
                shop, not to us. */}
            {isOwner && (
              <p className="mt-3.5 text-center text-[12.5px]" style={{ color: "var(--pg-faint)" }}>
                {t("browse.betaNote")}{" "}
                <Link
                  href="/pricing"
                  className="font-semibold underline-offset-2 hover:underline"
                  style={{ color: "var(--pg-fg)" }}
                >
                  {t("browse.seePricing")}
                </Link>
              </p>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  )
}

/* ── Features ──────────────────────────────────────────────────── */

function Features({ audience }: { audience: Audience }) {
  const t = useTranslations("marketing.landing")
  const tc = useTranslations("marketing.clientAccount")
  const icons = GRID_ICONS[audience]

  // Owner and barber read features.<audience>.* straight from the dictionary.
  // Client has no landing features block, so it composes the three
  // clientAccount feature pairs instead of inventing copy.
  const heading = audience === "client" ? tc("title") : t(`features.${audience}.heading`)
  const sub = audience === "client" ? tc("subtitle") : t(`features.${audience}.subLine1`)
  const cards =
    audience === "client"
      ? [
          { title: tc("featureBookTitle"), desc: tc("featureBookBody") },
          { title: tc("featureTrackTitle"), desc: tc("featureTrackBody") },
          { title: tc("featureConnectTitle"), desc: tc("featureConnectBody") },
        ]
      : (t.raw(`features.${audience}.grid`) as { title: string; desc: string }[])

  return (
    <section id="features" className="scroll-mt-24 px-5 py-20 sm:py-28">
      <div className="mx-auto max-w-5xl">
        <Reveal>
          <div className="mb-10 max-w-xl sm:mb-12">
            <Eyebrow>{t("features.subtitle")}</Eyebrow>
            <h2 className="mt-5 text-[clamp(2.15rem,4.8vw,3.35rem)] font-semibold leading-[1.04] tracking-[-0.035em]">
              {heading}
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed sm:mt-7 sm:text-base" style={{ color: "var(--pg-muted)" }}>
              {sub}
            </p>
          </div>
        </Reveal>

        <div key={audience} className="space-y-[3.3rem] sm:space-y-9">
          <FeatureProductDemos audience={audience} />

          {/* Keyed on audience so the cards re-run their reveal when the
              visitor switches — otherwise the copy swaps under a static grid
              and the change is easy to miss. */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {cards.map((f, i) => {
              const Icon = icons[i] ?? Network
              // A plain vertical stack of identical cards reads as a wall of
              // text on mobile — a slight alternating tilt (cancelled at
              // sm: and up, where the grid already gives it rhythm) is enough
              // to make the stack feel handled rather than auto-generated.
              const mobileTilt = i % 2 === 0 ? "-rotate-1" : "rotate-1"
              return (
                <Reveal key={f.title} delay={i * 60}>
                  <GlassCard className={`h-full p-6 ${mobileTilt} sm:rotate-0`}>
                    {/* White, not the green accent — the accent was competing
                        with the headline for attention across six cards. */}
                    <Icon className="h-[18px] w-[18px]" strokeWidth={1.6} style={{ color: "var(--pg-fg)" }} />
                    <h3 className="mt-5 text-[15px] font-semibold tracking-tight">{f.title}</h3>
                    <p className="mt-2 text-[13px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
                      {f.desc}
                    </p>
                  </GlassCard>
                </Reveal>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── Product demos (inside Features) ───────────────────────────── */

function FeatureProductDemos({ audience }: { audience: Audience }) {
  const t = useTranslations("marketing.landing")
  if (audience === "client") return null

  const images = PRODUCT_IMAGES[audience]
  const videos = PRODUCT_VIDEOS[audience]
  const products = t.raw(`features.${audience}.products`) as { title: string; kicker?: string; desc: string }[]

  return (
    <div className="grid grid-cols-1 gap-[3.3rem] sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 lg:gap-5">
      {products.map((p, i) => {
        // A card with a loop plays it; one without falls back to the still.
        const video = videos[i]
        return (
        <div key={p.title} className="w-full min-w-0">
          <ProductDemoCard
            delay={i * 70}
            playDelay={i * 70 + ZOOM_MS}
            playbackRate={video?.playbackRate}
            alt={p.title}
            src={video?.src}
            poster={video?.poster}
            imageSrc={video ? undefined : images[i]}
          />
          <h3 className="mt-5 text-[clamp(1.1rem,3.7vw,1.35rem)] font-semibold leading-[1.12] tracking-[-0.02em]">
            {p.title}
          </h3>
          {/* Optional one-liner under the title — what the title is claiming,
              in plain words, before the description. */}
          {p.kicker && (
            <p className="mt-1.5 text-[12px] font-medium tracking-[0.01em]" style={{ color: "var(--pg-faint)" }}>
              {p.kicker}
            </p>
          )}
          <p className="mt-2 text-[14px] leading-relaxed sm:text-[14.5px]" style={{ color: "var(--pg-muted)" }}>
            {p.desc}
          </p>
        </div>
        )
      })}
    </div>
  )
}

/* ── Money ─────────────────────────────────────────────────────── */

/* Payments section. One messaging system — "easiest way to pay" /
   "reduce payment risk" / "track payments live" — retold three ways,
   because the same rail means different things to each door: an owner is
   buying certainty on rent, a barber is buying speed plus points back, and
   a client is just buying a haircut in one tap.

   Visuals are drawn, not screenshotted, so the section stays honest when
   the product UI moves and so it survives the light/dark toggle. Index-
   aligned with money.<audience>.cards in the dictionary, same convention
   as GRID_ICONS and PRODUCT_IMAGES above. */
type MoneyVisual = "methods" | "live" | "phone" | "rewards" | "card"

const MONEY_VISUALS: Record<Audience, MoneyVisual[]> = {
  owner: ["methods", "live", "rewards"],
  barber: ["card", "rewards", "live"],
  client: ["phone", "rewards", "live"],
}

function Money({ audience }: { audience: Audience }) {
  const t = useTranslations("marketing.landing")
  const cards = t.raw(`money.${audience}.cards`) as { title: string; desc: string }[]
  const visuals = MONEY_VISUALS[audience]

  return (
    <section id="payments" className="scroll-mt-24 px-5 py-20 sm:py-28">
      <div className="mx-auto max-w-5xl">
        <Reveal>
          <div className="mb-12 max-w-xl sm:mb-14">
            <Eyebrow>{t("money.subtitle")}</Eyebrow>
            <h2 className="mt-5 text-[clamp(2.15rem,4.8vw,3.35rem)] font-semibold leading-[1.06] tracking-[-0.035em]">
              {t(`money.${audience}.heading`)}
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed sm:mt-7 sm:text-base" style={{ color: "var(--pg-muted)" }}>
              {t(`money.${audience}.sub`)}
            </p>
          </div>
        </Reveal>

        {/* Keyed on audience for the same reason the feature grid is: the
            copy swaps under a static layout otherwise and the switch reads
            as nothing happening. */}
        <div key={audience} className="grid gap-[3.3rem] md:grid-cols-3 md:gap-3">
          {cards.map((c, i) => (
            <Reveal key={c.title} delay={i * 80}>
              <GlassCard className="flex h-full flex-col p-6 sm:p-7">
                <h3 className="text-[clamp(1.25rem,3.8vw,1.35rem)] font-semibold leading-snug tracking-[-0.015em]">
                  {c.title}
                </h3>
                <p className="mt-4 text-[13.5px] leading-relaxed sm:text-[14px]" style={{ color: "var(--pg-muted)" }}>
                  {c.desc}
                </p>
                {/* The visual bleeds off the bottom edge of the card — it's a
                    hint of the surface, not a spec sheet. */}
                <div className="relative mt-7 h-56 flex-1 overflow-hidden">
                  <MoneyArt kind={visuals[i] ?? "methods"} />
                </div>
              </GlassCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

function MoneyArt({ kind }: { kind: MoneyVisual }) {
  if (kind === "phone") return <PhoneArt />
  if (kind === "card") return <CreditCardArt />
  if (kind === "live") return <LiveArt />
  if (kind === "rewards") return <RewardsArt />
  return <MethodsArt />
}

// Shared surface for the small drawn chips inside the art. Slightly brighter
// than GlassCard so the pieces read as sitting ON the card, not in it.
const ART_TILE: React.CSSProperties = {
  background: "var(--pg-card)",
  border: "1px solid var(--pg-card-edge)",
  boxShadow: "inset 0 1px 0 var(--pg-gloss)",
}

/* A phone, cropped by the card. The receipt inside is the client's side of a
   BOOTH payment — the thing an owner or barber never has to chase again. */
function PhoneArt() {
  const t = useTranslations("marketing.landing")
  return (
    <div className="absolute inset-x-0 bottom-0 flex justify-center">
      <div
        className="w-[196px] rounded-t-[30px] px-3 pb-6 pt-3"
        style={{
          background: "var(--pg-bg)",
          border: "1px solid var(--pg-card-edge)",
          borderBottom: "none",
          boxShadow: "0 -18px 50px -30px rgba(0,0,0,0.9)",
        }}
      >
        <div className="mx-auto mb-3 h-[5px] w-14 rounded-full" style={{ background: "var(--pg-card-edge)" }} />
        <div className="rounded-[18px] p-3.5" style={ART_TILE}>
          <div className="flex items-center gap-1.5">
            <span
              className="grid h-5 w-5 place-items-center rounded-[6px] text-[8px] font-bold"
              style={{ background: "var(--pg-invert-bg)", color: "var(--pg-invert-fg)" }}
            >
              B
            </span>
            <span className="text-[9px] tracking-[0.14em]" style={{ color: "var(--pg-faint)" }}>
              {t("money.art.receiptLabel")}
            </span>
          </div>
          <p className="mt-3 text-[11.5px] font-semibold leading-tight tracking-tight">
            {t("money.art.receiptShop")}
          </p>
          <p className="mt-0.5 text-[10px]" style={{ color: "var(--pg-muted)" }}>
            {t("money.art.receiptService")}
          </p>
          <div className="mt-3 flex items-baseline gap-1.5">
            <span className="text-[19px] font-semibold tracking-[-0.03em]">$45.00</span>
            <span className="text-[9.5px]" style={{ color: "var(--pg-faint)" }}>
              USD
            </span>
          </div>
          <span
            className="mt-3 inline-flex items-center gap-1.5 rounded-full px-2 py-[3px] text-[9px] font-medium"
            style={{ background: "rgba(52,199,89,0.14)", color: "rgb(90,214,124)" }}
          >
            <Check className="h-2.5 w-2.5" strokeWidth={2.5} />
            {t("money.art.paid")}
          </span>
        </div>
        <div
          className="mt-2.5 grid h-8 place-items-center rounded-[12px] text-[10px] font-medium"
          style={{ background: "var(--pg-invert-bg)", color: "var(--pg-invert-fg)" }}
        >
          {t("money.art.payButton")}
        </div>
      </div>
    </div>
  )
}

/* Cards on file + autopay — toggle demo with payment rails underneath. */
function MethodsArt() {
  return <AutopayArt />
}

/* The ledger, mid-move. Two shops fanned over each other, one settled and one
   still counting — the "money moves and you see it" beat. */
function LiveArt() {
  const t = useTranslations("marketing.landing")
  const rows = t.raw("money.art.live") as { name: string; meta: string; amount: string; state: string }[]
  return (
    <div className="absolute inset-x-0 bottom-0">
      {rows.map((r, i) => (
        <div
          key={r.name}
          className="rounded-[18px] p-3.5"
          style={{
            ...ART_TILE,
            marginLeft: i * 14,
            marginTop: i === 0 ? 0 : -10,
            transform: `rotate(${i === 0 ? -1.4 : 1.1}deg)`,
            boxShadow: "inset 0 1px 0 var(--pg-gloss), 0 18px 40px -28px rgba(0,0,0,0.95)",
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-[12px] font-semibold tracking-tight">{r.name}</p>
            <span
              className="shrink-0 rounded-full px-2 py-[3px] text-[8.5px] font-medium"
              style={
                r.state === "paid"
                  ? { background: "rgba(52,199,89,0.14)", color: "rgb(90,214,124)" }
                  : { background: "rgba(96,165,250,0.16)", color: "rgb(125,185,255)" }
              }
            >
              {r.state === "paid" ? t("money.art.paid") : t("money.art.due")}
            </span>
          </div>
          <div className="mt-2.5 flex items-center justify-between gap-2">
            <span className="text-[10px]" style={{ color: "var(--pg-faint)" }}>
              {r.meta}
            </span>
            <span className="text-[13px] font-semibold tracking-[-0.02em]">{r.amount}</span>
          </div>
          <div className="mt-2.5 h-[3px] w-full overflow-hidden rounded-full" style={{ background: "var(--pg-card-edge)" }}>
            <div
              className="h-full rounded-full"
              style={{
                width: r.state === "paid" ? "100%" : "62%",
                background: r.state === "paid" ? "rgb(90,214,124)" : "rgb(125,185,255)",
              }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

/* Points climbing — same rewards hero as the app: balance counts up, tier
   bar fills, tier-metal sweep on first reveal. */
function RewardsArt() {
  return <RewardsProgressArt />
}

/* ── Hiring ────────────────────────────────────────────────────── */

function Hiring({ audience }: { audience: Audience }) {
  const t = useTranslations("marketing.landing")
  // Hiring is a pro surface — an owner posts a chair, a barber answers one.
  // Neither reading applies to a booking client, so it's hidden there.
  if (audience === "client") return null
  const a = audience

  return (
    <section className="px-5 pt-10 pb-6 sm:py-28">
      <Reveal>
        <GlassCard className="mx-auto max-w-5xl overflow-hidden p-8 sm:p-14">
          <span
            className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[9px] font-medium uppercase tracking-[0.18em]"
            style={{ background: "var(--pg-card)", border: "1px solid var(--pg-card-edge)", color: "var(--pg-muted)" }}
          >
            <PulseDot className="h-[3px] w-1.5" />
            {t("hiring.badge")}
          </span>

          <h2
            className="mt-7 text-[clamp(2.25rem,7vw,4.5rem)] font-light leading-[1.03] tracking-[-0.035em]"
            style={{ color: "var(--pg-muted)" }}
          >
            {t(`hiring.${a}.heading`)}
          </h2>
          <p className="text-[clamp(1.5rem,4.6vw,3rem)] font-medium leading-[1.05] tracking-[-0.035em]">
            {t(`hiring.${a}.subheading`)}
          </p>

          <p className="mt-6 max-w-md text-[14px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
            {t(`hiring.${a}.bodyLine1`)}
            <br />
            {t(`hiring.${a}.bodyLine2`)}
          </p>

          <Link
            href="/hiring"
            className="mt-8 inline-flex items-center gap-2 text-[14px] font-medium transition-opacity duration-200 hover:opacity-60"
          >
            <Plus className="h-4 w-4" strokeWidth={1.75} />
            {t(`hiring.${a}.cta`)}
          </Link>
        </GlassCard>
      </Reveal>
    </section>
  )
}

/* ── Guest Pass ────────────────────────────────────────────────── */

function GuestPass({ audience }: { audience: Audience }) {
  const t = useTranslations("marketing.landing")
  // Same two-sided read as Hiring: the owner opens a chair for a few days, the
  // barber works one. A booking client rents nothing, so it isn't for them.
  if (audience === "client") return null
  const a = audience
  const isOwner = a === "owner"

  return (
    <section className="px-5 pt-6 pb-20 sm:py-28">
      <Reveal>
        <GlassCard className="mx-auto max-w-5xl overflow-hidden p-8 sm:p-14">
          {/* The flex lives HERE, not on GlassCard: the card wraps its children
              in a div of its own, so a layout class on the outside never
              reaches them — it stacked the copy and the card instead. */}
          <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:gap-16">
          <div className="min-w-0 flex-1">
          <span
            className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[9px] font-medium uppercase tracking-[0.18em]"
            style={{ background: "var(--pg-card)", border: "1px solid var(--pg-card-edge)", color: "var(--pg-muted)" }}
          >
            <PulseDot className="h-[3px] w-1.5" />
            {t("guestPass.badge")}
          </span>

          <h2
            className="mt-7 text-[clamp(2.25rem,7vw,4.5rem)] font-light leading-[1.03] tracking-[-0.035em]"
            style={{ color: "var(--pg-muted)" }}
          >
            {t(`guestPass.${a}.heading`)}
          </h2>
          <p className="text-[clamp(1.5rem,4.6vw,3rem)] font-medium leading-[1.05] tracking-[-0.035em]">
            {t(`guestPass.${a}.subheading`)}
          </p>

          <p className="mt-6 max-w-md text-[14px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
            {t(`guestPass.${a}.bodyLine1`)}
            <br />
            {t(`guestPass.${a}.bodyLine2`)}
          </p>

          <Link
            href={isOwner ? "/dashboard/owner" : "/listings"}
            className="mt-8 inline-flex items-center gap-2 text-[14px] font-medium transition-opacity duration-200 hover:opacity-60"
          >
            {isOwner ? <Plus className="h-4 w-4" strokeWidth={1.75} /> : <ArrowRight className="h-4 w-4" strokeWidth={1.75} />}
            {t(`guestPass.${a}.cta`)}
          </Link>
          </div>

          {/* The card itself, not a picture of it — same face, same ear, same
              chair as the one on a listing, and it turns over when you ask it
              to. The section is about a card; showing the card beats
              describing it. */}
          <div className="shrink-0 self-center">
            <GuestPassCard audience={a} />
          </div>
          </div>
        </GlassCard>
      </Reveal>
    </section>
  )
}

/* ── Booking platforms ─────────────────────────────────────────── */

function Booking({ audience }: { audience: Audience }) {
  const t = useTranslations("marketing.landing")
  // Booking-link passthrough is something a barber or shop configures on their
  // own profile. A client just books — the platform list is noise to them.
  if (audience === "client") return null
  return (
    <section className="px-5 py-20 sm:py-28">
      <div className="mx-auto grid max-w-5xl items-center gap-8 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <div>
            <Eyebrow>{t("booking.badge")}</Eyebrow>
            <h2 className="mt-4 text-[clamp(1.7rem,4vw,2.4rem)] font-semibold leading-[1.1] tracking-[-0.035em]">
              {t("booking.headingLine1")}
              <br />
              {t("booking.headingLine2")}
            </h2>
            <p className="mt-5 max-w-md text-[14px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
              {t.rich("booking.body1", {
                name: (chunks) => <span style={{ color: "var(--pg-fg)" }}>{chunks}</span>,
              })}{" "}
              {t("booking.body2")}
            </p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          {/* The logos float free — no card each. They were five glass tiles
              in a 2-up grid, which read as a product grid with nothing to
              buy. A loose cluster of marks says "these all work here". */}
          {/* Stacked (phone, tablet): three marks over two, centred, 2rem under
              the copy — the max-width is exactly three logos plus two gaps, so
              the wrap lands there. Side by side (lg+): one row of five. */}
          <div className="mx-auto flex max-w-[16.5rem] flex-wrap items-center justify-center gap-x-9 gap-y-7 lg:mx-0 lg:max-w-none lg:justify-start">
            {PLATFORMS.map((p, i) => (
              <a
                key={p.alt}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                title={p.alt}
                className="group block transition-transform duration-300 hover:-translate-y-1 hover:scale-110"
                style={{ animation: `pgFloat ${4 + i * 0.45}s ease-in-out ${i * 0.15}s infinite alternate` }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={p.src}
                  alt={p.alt}
                  className="h-16 w-16 rounded-full object-contain shadow-[0_18px_40px_-18px_rgba(0,0,0,0.7)] transition-shadow duration-300 group-hover:shadow-[0_24px_50px_-16px_rgba(0,0,0,0.85)]"
                />
              </a>
            ))}
          </div>
        </Reveal>
      </div>

      <style jsx>{`
        @keyframes pgFloat {
          from {
            transform: translateY(-6px);
          }
          to {
            transform: translateY(6px);
          }
        }
      `}</style>
    </section>
  )
}

/* ── Support ───────────────────────────────────────────────────── */

/* "Got a question?" — the low-commitment exit from the page. Renders in all
   three modes because every door has the same three questions at the end:
   how do I start, can I see it first, and where do I read more.

   The demo row is deliberately inert: the walkthrough isn't recorded yet, so
   it renders as a labelled placeholder rather than a link into nothing. When
   the video exists it gains an href and the badge comes off. */
function Support({ audience }: { audience: Audience }) {
  const t = useTranslations("marketing.landing")
  const rows = [
    { key: "start", href: SIGNUP_HREF[audience] },
    { key: "demo", href: null },
    { key: "guides", href: "/blog?type=guides" },
  ] as const

  return (
    <section className="px-5 py-20 sm:py-28">
      <div className="mx-auto grid max-w-5xl gap-10 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <div className="lg:sticky lg:top-28">
            <h2 className="text-[clamp(1.9rem,4.4vw,2.9rem)] font-semibold leading-[1.1] tracking-[-0.035em]">
              {t("support.heading")}
            </h2>
            <p className="mt-5 max-w-sm text-[15px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
              {t("support.sub")}
            </p>
          </div>
        </Reveal>

        <div key={audience} className="flex flex-col gap-3">
          {rows.map((row, i) => {
            const title = t(`support.${row.key}.title`)
            const desc = t(`support.${row.key}.desc`)
            const body = (
              <>
                <div className="min-w-0">
                  <div className="flex items-center gap-2.5">
                    <h3 className="text-[16px] font-semibold tracking-tight">{title}</h3>
                    {!row.href && (
                      <span
                        className="rounded-full px-2 py-[3px] text-[8.5px] font-medium uppercase tracking-[0.14em]"
                        style={{ background: "var(--pg-card)", color: "var(--pg-faint)" }}
                      >
                        {t("support.soon")}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-[13.5px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
                    {desc}
                  </p>
                </div>
                <ArrowRight
                  className="mt-1 h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-1"
                  strokeWidth={1.6}
                  style={{ color: "var(--pg-faint)" }}
                />
              </>
            )

            return (
              <Reveal key={row.key} delay={i * 70}>
                {row.href ? (
                  <Link href={row.href} className="group block">
                    <GlassCard className="flex items-start justify-between gap-6 p-6 transition-opacity duration-300 group-hover:opacity-80 sm:p-7">
                      {body}
                    </GlassCard>
                  </Link>
                ) : (
                  <GlassCard className="flex items-start justify-between gap-6 p-6 opacity-60 sm:p-7">{body}</GlassCard>
                )}
              </Reveal>
            )
          })}
        </div>
      </div>
    </section>
  )
}

/* ── Final CTA ─────────────────────────────────────────────────── */

function FinalCta({ audience }: { audience: Audience }) {
  const t = useTranslations("marketing.landing")
  return (
    <section className="px-5 py-24 sm:py-36">
      <Reveal>
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-[clamp(2rem,5.5vw,3.25rem)] font-semibold leading-[1.03] tracking-[-0.04em]">
            {t("cta.heading")}
          </h2>
          <p className="mx-auto mt-5 max-w-md text-[15px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
            {t("cta.subtitle")}
          </p>
          {/* Sized off the house Button (h-10 / text-sm / font-medium) rather
              than the oversized hero pills — this is a footer-adjacent close,
              not the top of the page. */}
          <div className="mt-9 flex flex-col items-center justify-center gap-2.5 sm:flex-row">
            <Link
              href={SIGNUP_HREF[audience]}
              className="group inline-flex h-10 items-center justify-center gap-1.5 rounded-full px-5 text-sm font-medium transition-transform duration-200 hover:scale-[1.02] active:scale-95"
              style={{ background: "var(--pg-invert-bg)", color: "var(--pg-invert-fg)" }}
            >
              {t("cta.loggedOut")}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
            {/* Demos are a shop sales motion — no point offering one to a
                barber looking for a chair or a client booking a cut. */}
            {audience === "owner" && (
              <a
                href="https://cal.com/boothdemo/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center justify-center gap-1.5 rounded-full px-5 text-sm font-medium backdrop-blur-xl"
                style={{ background: "var(--pg-card)", border: "1px solid var(--pg-card-edge)", color: "var(--pg-fg)" }}
              >
                {t("cta.bookDemo")}
                <ArrowUpRight className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>
      </Reveal>
    </section>
  )
}

/* ── Footer ────────────────────────────────────────────────────── */

/* Same `only` convention as the nav: absent means every audience sees it.
   The footer follows the switch so a client isn't sent to the job board and
   an owner isn't pointed at the shops they already run. */
type FooterLink = { labelKey: string; href: string; external?: boolean; only?: Audience[] }

const FOOTER_COLUMNS: { titleKey: string; links: FooterLink[] }[] = [
  {
    titleKey: "getStarted",
    links: [
      { labelKey: "links.signUp", href: "/auth/signup", only: ["barber", "owner"] },
      { labelKey: "links.signUp", href: "/auth/signup/client", only: ["client"] },
      { labelKey: "links.logIn", href: "/auth/login" },
      { labelKey: "links.pricing", href: "/pricing", only: ["owner"] },
    ],
  },
  {
    titleKey: "discover",
    links: [
      { labelKey: "links.browseShops", href: "/listings", only: ["barber", "client"] },
      { labelKey: "links.browseBarbers", href: "/barbers", only: ["owner"] },
      { labelKey: "links.jobBoard", href: "/hiring", only: ["barber", "owner"] },
      { labelKey: "links.rewards", href: "/rewards", only: ["barber", "owner"] },
    ],
  },
  {
    titleKey: "company",
    links: [
      { labelKey: "links.articles", href: "/blog" },
      { labelKey: "links.press", href: "/press" },
      { labelKey: "links.traction", href: "/traction", only: ["barber", "owner"] },
      { labelKey: "links.buildingInPublic", href: "/changelog", only: ["barber", "owner"] },
      {
        labelKey: "links.bookDemo",
        href: "https://cal.com/boothdemo/30min",
        external: true,
        only: ["owner"],
      },
    ],
  },
  {
    titleKey: "legal",
    links: [
      { labelKey: "links.privacy", href: "/privacy" },
      { labelKey: "links.terms", href: "/terms" },
      { labelKey: "links.refunds", href: "/refunds" },
    ],
  },
]

function Footer({ mode, audience }: { mode: GlassNavMode; audience: Audience }) {
  const t = useTranslations("marketing.footer")
  const columns = FOOTER_COLUMNS.map((col) => ({
    ...col,
    links: col.links.filter((l) => !l.only || l.only.includes(audience)),
  })).filter((col) => col.links.length > 0)
  return (
    <footer
      // pb-28, not pb-16: the Ask bubble is fixed bottom-left (bottom-5, 56px
      // tall) and sat on the copyright line. The row now clears it.
      className="px-6 pb-28 pt-20 sm:px-10"
      style={{
        borderTop: "1px solid var(--pg-line)",
        // The page background is ink (#0B0B0D) in dark mode and cream
        // in light, so the footer takes the true end of each scale — black,
        // white — or it just blends into the section above it with nothing
        // to mark where the page ends.
        background: mode === "dark" ? "#000000" : "#FFFFFF",
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <div className="grid grid-cols-2 gap-y-10 md:grid-cols-12 md:gap-x-10">
          <div className="col-span-2 md:col-span-4">
            {/* Real wordmark, not letterspaced text. Unlike the nav pill (dark
                in both modes) the footer sits on the page background, so the
                variant follows the page's own mode: white ink on black,
                black ink on cream. */}
            <Link href="/" aria-label="BOOTH home" className="inline-block transition-opacity hover:opacity-70">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={mode === "dark" ? "/images/booth-wordmark-dark.webp" : "/images/booth-wordmark-light.webp"}
                alt="BOOTH"
                width={80}
                height={14}
                className="h-[14px] w-auto"
              />
            </Link>
            <p className="mt-4 max-w-xs text-[12px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
              {t("tagline")}
            </p>
          </div>

          {columns.map((col) => (
            <div key={col.titleKey} className="md:col-span-2">
              <h4
                className="mb-5 border-b pb-3 font-mono text-[10px] uppercase tracking-[0.16em]"
                style={{ borderColor: "var(--pg-line)" }}
              >
                {t(col.titleKey)}
              </h4>
              <ul className="space-y-3 text-[13px]" style={{ color: "var(--pg-muted)" }}>
                {col.links.map((l) => (
                  <li key={l.href}>
                    {l.external ? (
                      <a href={l.href} className="transition-opacity hover:opacity-60">
                        {t(l.labelKey)}
                      </a>
                    ) : (
                      <Link href={l.href} className="transition-opacity hover:opacity-60">
                        {t(l.labelKey)}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div
          className="mt-16 flex flex-col gap-3 border-t pt-7 font-mono text-[10px] uppercase tracking-[0.16em] sm:flex-row sm:items-center sm:justify-between"
          style={{ borderColor: "var(--pg-line)", color: "var(--pg-faint)" }}
        >
          <p>
            © {new Date().getFullYear()} BOOTH · {t("rights")}
          </p>
        </div>
      </div>
    </footer>
  )
}

/* ── Shared primitives ─────────────────────────────────────────── */

